import {
  effectiveReference, formatBack, formatTrue, MeasureBearingUnit, MeasureNorthReference, NORTH_LETTERS, NorthCorrections,
} from './model/bearings';
import { accuracy, distance, elevationChange, label as pillLabel, MeasureUnits } from './model/formatters';
import { formatGridReference, MEASURE_GRID_FORMATS, MeasureGridFormat } from './model/gridReference';
import { ascent, change } from './model/ticks';
import type { MeasureMeasurement, MeasureSession } from './model/session';
import type { MeasureSettings } from './settings';
import { MeasureView, PROFILE_VIEWBOX } from './viewTypes';

type Translate = (key: string, params?: Record<string, unknown>) => string;

// Every label is a literal t('…') call: the client bundle (lib/i18n_client_bundle.rb) ships only
// the keys it finds written out in app/javascript, so a key built at runtime would never arrive.
const gridNames = (t: Translate): Record<MeasureGridFormat, string> => ({
  IrishGrid: t('measure_tool.grid_formats.irish_grid'),
  Itm: t('measure_tool.grid_formats.itm'),
  Osgb: t('measure_tool.grid_formats.osgb'),
  Utm: t('measure_tool.grid_formats.utm'),
  Mgrs: t('measure_tool.grid_formats.mgrs'),
  LatLong: t('measure_tool.grid_formats.lat_long'),
});

const lockText = (t: Translate) => ({
  Free: { title: t('measure_tool.lock_free'), detail: t('measure_tool.lock_free_detail'), pill: t('measure_tool.lock_pill_free') },
  Distance: { title: t('measure_tool.lock_distance'), detail: t('measure_tool.lock_distance_detail'), pill: t('measure_tool.lock_pill_distance') },
  Bearing: { title: t('measure_tool.lock_bearing'), detail: t('measure_tool.lock_bearing_detail'), pill: t('measure_tool.lock_pill_bearing') },
});

const northText = (t: Translate) => ({
  True: { label: t('measure_tool.north_true'), note: t('measure_tool.range_note_true') },
  Magnetic: { label: t('measure_tool.north_magnetic'), note: t('measure_tool.range_note_magnetic') },
  Grid: { label: t('measure_tool.north_grid'), note: t('measure_tool.range_note_grid') },
});

export type MeasureElevationState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; elevations: number[] };

export type MeasureLocationState = { status: 'idle' | 'finding' | 'denied' | 'unavailable'; accuracy?: number };

export interface MeasureViewInput {
  t: Translate;
  narrow: boolean;
  session: MeasureSession;
  /** The placed measurement, or the cursor preview's while placing. */
  measurement: MeasureMeasurement | null;
  settings: MeasureSettings;
  units: MeasureUnits;
  corrections: NorthCorrections;
  elevation: MeasureElevationState;
  profileHover: number | null;
  location: MeasureLocationState;
  routeAvailable: boolean;
  copied: boolean;
  settingsOpen: boolean;
  toast: string;
  /** The walk's time at the user's hike pace, already formatted ("1 h 20 min"). */
  walkingTime: (distanceMeters: number, ascentMeters: number) => string;
}

export function panelStep(session: MeasureSession): MeasureView['step'] {
  if (session.step === 'Start') return 'start';
  if (session.step === 'PickStart') return 'pickStart';
  if (session.rangeSheetOpen) return 'range';
  return session.target ? 'placed' : 'placing';
}

const gridName = (t: Translate, format: MeasureGridFormat) => gridNames(t)[format];

/** The grid reference of a point, or "Outside the … grid" when the point is outside the chosen grid. */
export function gridText(t: Translate, format: MeasureGridFormat, lat: number, lng: number): string {
  return formatGridReference(format, lat, lng) ?? t('measure_tool.outside_grid', { format: gridName(t, format) });
}

/** The profile strip's line and area paths in the fixed viewBox, low at the bottom. */
export function profilePaths(elevations: number[]): { line: string; area: string } {
  const { width: W, height: H } = PROFILE_VIEWBOX;
  if (elevations.length < 2) return { line: '', area: '' };
  const low = Math.min(...elevations);
  const span = Math.max(4, Math.max(...elevations) - low);
  const pts = elevations.map((e, i) => `${((i / (elevations.length - 1)) * W).toFixed(1)},${(H - 3 - ((e - low) / span) * (H - 8)).toFixed(1)}`);
  return { line: `M${pts.join('L')}`, area: `M${pts.join('L')}L${W},${H}L0,${H}Z` };
}

export function northCaption(t: Translate, settings: MeasureSettings, corrections: NorthCorrections): string {
  const format = gridName(t, settings.gridFormat);
  if (settings.northReference === 'Magnetic') {
    const value = Math.abs(corrections.declination).toFixed(1);
    const side = corrections.declination >= 0 ? t('measure_tool.declination_east', { value }) : t('measure_tool.declination_west', { value });
    return t('measure_tool.north_caption_magnetic', { value: side });
  }
  if (settings.northReference === 'Grid') {
    if (!corrections.gridAvailable) return t('measure_tool.north_caption_grid_fallback', { format });
    return t('measure_tool.north_caption_grid', { format, value: `${Math.abs(corrections.convergence).toFixed(1)}°` });
  }
  return t('measure_tool.north_caption_true');
}

export function measureView(input: MeasureViewInput): MeasureView {
  const { t, narrow, session, measurement, settings, units, corrections, elevation } = input;
  const step = panelStep(session);
  const reference: MeasureNorthReference = effectiveReference(corrections, settings.northReference);
  const unit: MeasureBearingUnit = settings.bearingUnit;
  const ready = elevation.status === 'ready' ? elevation.elevations : null;

  const stats = measurement ? [
    { label: t('measure_tool.stat_bearing'), value: formatTrue(measurement.trueBearing, unit, reference, corrections), accent: true },
    { label: t('measure_tool.stat_distance'), value: distance(measurement.distance, units), accent: false },
    { label: t('measure_tool.stat_elevation'), value: ready ? elevationChange(change(ready), units.small) : '–', accent: false },
  ] : [];

  const titles: Record<string, string> = {
    start: t('measure_tool.where_from'),
    pickStart: narrow ? t('measure_tool.set_start_tap') : t('measure_tool.set_start_click'),
    placing: narrow ? t('measure_tool.drop_point_tap') : t('measure_tool.drop_point_click'),
    range: t('measure_tool.enter_range_bearing'),
    placed: '',
  };

  const location = input.location;
  const myLocationDetail = location.status === 'finding' ? t('measure_tool.my_location_finding')
    : location.status === 'denied' ? t('measure_tool.my_location_denied')
      : location.status === 'unavailable' ? t('measure_tool.my_location_unavailable')
        : location.accuracy !== undefined ? t('measure_tool.my_location_detail', { accuracy: accuracy(location.accuracy, units.small) })
          : t('measure_tool.my_location_detail', { accuracy: '…' });

  const isMyLocation = session.startSource === 'MyLocation';
  const placed = step === 'placed' && measurement ? placedView(input, measurement, reference, ready) : null;
  const locks = lockText(t);
  const norths = northText(t);
  const lockItems = (['Free', 'Distance', 'Bearing'] as const).map((mode) => ({
    mode,
    title: locks[mode].title,
    detail: locks[mode].detail,
    kbd: mode === 'Distance' ? 'Alt' : mode === 'Bearing' ? 'Shift' : '',
    on: session.lock === mode,
  }));
  const routeOn = input.routeAvailable && session.routeOn;

  return {
    narrow,
    step,
    title: titles[step],
    stats,
    isPreview: step === 'placing' && !!measurement,
    myLocation: { detail: myLocationDetail, disabled: location.status === 'finding' || location.status === 'denied' },
    pickDetail: narrow ? t('measure_tool.choose_point_detail_tap') : t('measure_tool.choose_point_detail_click'),
    showRightClickTip: !narrow,
    from: session.start ? {
      name: isMyLocation ? t('measure_tool.from_my_location') : t('measure_tool.from_map_point'),
      grid: gridText(t, settings.gridFormat, session.start.lat, session.start.lng),
      isMyLocation,
    } : null,
    range: {
      intro: isMyLocation ? t('measure_tool.range_intro_my_location') : t('measure_tool.range_intro_map_point'),
      distanceLabel: t('measure_tool.range_distance_label'),
      bearingLabel: unit === 'Mils'
        ? t('measure_tool.range_bearing_label_mils', { north: NORTH_LETTERS[reference] })
        : t('measure_tool.range_bearing_label_degrees', { north: NORTH_LETTERS[reference] }),
      note: norths[reference].note,
    },
    placed,
    pills: {
      // Snap to route can be chosen before either point is down (it decides where the start
      // lands and what the cursor tip reads), so it shows from picking the start onwards.
      show: step === 'placed' || (input.routeAvailable && (step === 'pickStart' || step === 'placing')),
      snap: { show: input.routeAvailable, on: routeOn },
      createHike: step === 'placed' && routeOn && !!measurement?.routeMode,
      lock: {
        show: step === 'placed' && !routeOn,
        title: locks[session.lock].pill,
        icon: session.lock === 'Free' ? 'lock_open' : 'lock',
        locked: session.lock !== 'Free',
        menuOpen: session.lockMenuOpen,
        items: lockItems,
      },
    },
    settings: {
      open: input.settingsOpen,
      north: (['True', 'Magnetic', 'Grid'] as const).map((value) => ({
        value, label: norths[value].label, on: settings.northReference === value,
      })),
      northCaption: northCaption(t, settings, corrections),
      units: [
        { value: 'Degrees' as const, label: t('measure_tool.degrees'), on: unit === 'Degrees' },
        { value: 'Mils' as const, label: t('measure_tool.mils'), on: unit === 'Mils' },
      ],
      gridFormat: settings.gridFormat,
      gridFormats: MEASURE_GRID_FORMATS.map((value) => ({ value, label: gridName(t, value) })),
      tolerance: `±${settings.toleranceMeters} m`,
      showKeyboard: !narrow,
    },
    toast: input.toast,
  };
}

function placedView(
  input: MeasureViewInput,
  measurement: MeasureMeasurement,
  reference: MeasureNorthReference,
  elevations: number[] | null,
): NonNullable<MeasureView['placed']> {
  const { t, settings, units, corrections } = input;
  const climb = elevations ? ascent(elevations) : 0;
  const back = formatBack(measurement.trueBearing, settings.bearingUnit, reference, corrections);
  const paths = elevations ? profilePaths(elevations) : { line: '', area: '' };
  let hover: NonNullable<MeasureView['placed']>['profile']['hover'] = null;
  if (elevations && input.profileHover !== null && input.profileHover !== undefined) {
    const fraction = input.profileHover;
    const index = Math.round(fraction * (elevations.length - 1));
    hover = {
      x: Number((fraction * PROFILE_VIEWBOX.width).toFixed(1)),
      text: `${distance(measurement.distance * fraction, units)} · ${accuracy(elevations[index], units.small)}`,
      leftPercent: Number((fraction * 100).toFixed(1)),
      shift: fraction < 0.2 ? '-10%' : fraction > 0.8 ? '-90%' : '-50%',
    };
  }
  return {
    gridText: gridText(t, settings.gridFormat, measurement.target.lat, measurement.target.lng),
    gridTitle: t('measure_tool.grid_title', { format: gridName(t, settings.gridFormat) }),
    copyLabel: input.copied ? t('measure_tool.copied') : t('measure_tool.copy'),
    copied: input.copied,
    timeBack: t('measure_tool.time_back', { time: input.walkingTime(measurement.distance, climb), bearing: back }),
    profile: {
      ...paths,
      label: elevations
        ? t('measure_tool.profile_a11y', {
          low: accuracy(Math.min(...elevations), units.small),
          high: accuracy(Math.max(...elevations), units.small),
          climb: accuracy(climb, units.small),
        })
        : input.elevation.status === 'loading' ? t('measure_tool.profile_loading') : t('measure_tool.profile_unavailable'),
      status: input.elevation.status,
      hover,
    },
  };
}

/** The map pill over the measured point: "312 m · 047°M". */
export function pinLabel(measurement: MeasureMeasurement, settings: MeasureSettings, units: MeasureUnits, corrections: NorthCorrections): string {
  const reference = effectiveReference(corrections, settings.northReference);
  return pillLabel(measurement.distance, units, formatTrue(measurement.trueBearing, settings.bearingUnit, reference, corrections));
}
